<template>
  <div class="bg-white rounded-lg shadow-sm overflow-hidden">
    <div class="p-4">
      <div class="h-full rounded-2xl p-4 relative" style="background-color: #FFF9C4;">
        <div class="flex items-center justify-between mb-2">
          <div>
            <h4 class="text-sm font-medium text-gray-900 flex items-center gap-1">
              <CalendarFold class="w-4 h-4" />
              Week {{ week }} Notes
            </h4>
          </div>
        </div>

        <!-- Notes Display/Edit -->
        <div v-if="isEditing" class="space-y-4">
          <textarea
            v-model="noteInput"
            rows="4"
            class="w-full px-0 py-0 pb-4 border-none outline-none shadow-none bg-transparent text-sm resize-none min-h-[100px] focus:ring-0 focus:border-0 focus:outline-none"
            :disabled="isSaving"
            @keydown.enter.ctrl.prevent="onSaveShortcut"
            @keydown.enter.meta.prevent="onSaveShortcut"
            @input="autoGrow"
            ref="noteTextarea"
            placeholder="Add notes for this week..."
          ></textarea>
        </div>
        <div v-else-if="noteInput" class="text-sm text-black whitespace-pre-wrap min-h-[60px] cursor-pointer text-wrap pb-[1.37rem]" @click="startEditing">
          {{ noteInput }}
        </div>
        <div v-else class="text-sm text-black opacity-40 whitespace-pre-wrap min-h-[60px] cursor-pointer" @click="startEditing">
          No notes added yet. Click to add notes...
        </div>
        
        <div class="absolute bottom-2 left-2 flex items-center space-x-2 p-2">
          <span v-if="notes.length > 0" class="text-xs text-gray-500">
            {{ formatDate(notes[notes.length - 1].updated_at) }} @{{ notes[notes.length - 1].created_by.username }}
          </span>
          <span v-if="savingStatus === 'saving'" class="text-xs text-gray-500 flex items-center gap-1">
            <Loader2 class="w-3 h-3 animate-spin" />
            Saving...
          </span>
          <span v-else-if="savingStatus === 'saved'" class="text-xs text-green-600">
            Saved
          </span>
          <span v-else-if="savingStatus === 'error'" class="text-xs text-red-600">
            Failed to save
          </span>
        </div>
        
        <button
          v-if="isEditing && isDirty"
          @click="save"
          :disabled="isSaving"
          class="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black flex items-center justify-center text-white shadow-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed z-10"
        >
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import { Save, Loader2, CalendarFold } from 'lucide-vue-next'
import { formatDate } from '@/utils/formatDate'

const props = defineProps<{
  notes: any[]
  week: number | null
  isLoading?: boolean
}>()

const emit = defineEmits<{
  'save-note': [note: string]
}>()

const isEditing = ref(false)
const isSaving = ref(false)
const noteInput = ref('')
const noteTextarea = ref<HTMLTextAreaElement | null>(null)
const originalNote = ref('')
const isDirty = computed(() => noteInput.value !== originalNote.value)
const savingStatus = ref<'saved' | 'saving' | 'error' | ''>('')

// Watch for changes in notes prop
watch(
  () => props.notes,
  (newNotes) => {
    const joined = newNotes?.map((n) => n.note).join('\n\n') || ''
    noteInput.value = joined
    originalNote.value = joined
  },
  { immediate: true, deep: true }
)

const startEditing = async () => {
  isEditing.value = true
  await nextTick()
  if (noteTextarea.value) {
    noteTextarea.value.focus()
    autoGrow({ target: noteTextarea.value } as any)
  }
}

const onSaveShortcut = () => {
  if (isDirty.value && !isSaving.value) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    save()
  }
}

const save = async () => {
  if (!isDirty.value) return
  isSaving.value = true
  savingStatus.value = 'saving'
  
  try {
    emit('save-note', noteInput.value)
    isEditing.value = false
    originalNote.value = noteInput.value
    savingStatus.value = 'saved'
    
    setTimeout(() => {
      savingStatus.value = ''
    }, 2000)
  } catch (error) {
    console.error('Failed to save note:', error)
    savingStatus.value = 'error'
  } finally {
    isSaving.value = false
  }
}

// Auto-grow textarea
const autoGrow = (e: any) => {
  const el = e.target as HTMLTextAreaElement
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}
</script>